import {computed, Injectable, signal} from '@angular/core';
import {TaAppStore} from '../state/clcta-store';
import {
  AdminService,
  BasicTaDetails,
  BasicTaDetailsRestResponse, ComplianceDashboardTabResultRestResponse, FileDetailsRestResponse, FileService,
  FullTaDetails, FullTaDetailsRestResponse, ItineraryItemDetails, LocationOption,
  SubmissionApproversDetails, SubmissionApproversDetailsRestResponse, SubmissionBatchResultRestResponse,
  SubmissionService,
  TaWorkflowType,
  TravellerDetails,
  UserTravelItinerary, UserTravelItineraryRestResponse, UserType,
} from '../api';
import {LoadingService} from "../services/loading.service";
import {Router} from "@angular/router";
import {
  ComplianceDashboardTab,
  CreateTaStep,
  CreateTaStepType,
  FlightTripType,
  flightTripTypeFromDates,
  isFlightOnlyTa,
  ValidatorResponse,
} from '../util/app.types';
import {QueueService} from '../services/queue.service';
import {defer, EMPTY, finalize, Observable, of, tap} from 'rxjs';
import {HttpEventType} from '@angular/common/http';
import {PermissionService} from '../services/permission.service';
import {ErrorMessageService} from '../services/error-message.service';
import {isDayTripItinerary} from '../util/itinerary.utils';
import {resolveItineraryTravellerId} from '../util/general.utils';


@Injectable({
  providedIn: 'root'
})
export class TaSubmissionDatasource {
  taSubmissionReduxCopy = computed<FullTaDetails | undefined>(() => this.store.taSubmission());

  private itineraryListMap: Map<string, UserTravelItinerary> = new Map<string, UserTravelItinerary>();
  private itineraryTravellerMap: Map<string, string[]> = new Map<string, string[]>();
  private itineraryLoadingInFlight = new Set<string>();
  // Bumped by clearItineraryCaches so an itinerary fetch started before it is dropped.
  private itineraryCacheGeneration = 0;
  // Latest start/end date save, and the traveller whose itinerary to refetch once it lands.
  private dateSaveSeq = 0;
  private dateSaveTravellerId: string | undefined;
  private taLoadInFlight = new Set<string>();
  private submitInFlight = new Set<string>();
  primaryLinkedUserTravellers = signal<string[]>([]);
  allItineraries = signal<UserTravelItinerary[]>([]);
  // Server drops the primary traveller when all are casual, so submit fails. Delegate TAs are exempt.
  hasOnlyCasualTravellers = computed<boolean>(() => {
    const ta = this.store.taSubmission();
    const travellers = ta?.travellers ?? [];
    return !!ta && !ta.delegate && travellers.length > 0
      && travellers.every(t => t.details?.userType === UserType.Casual);
  });

  /**
   * Why the last loadServerTaSubmission failed, keyed by the id it was asked for, or undefined
   * while a load is in flight or after one succeeded. The server answers "Submission not found"
   * with HTTP 200 and success:false, so callers that gate their view on the TA arriving have no
   * other way to tell a dead id from a slow one.
   */
  taLoadError = signal<{id: string; message: string} | undefined>(undefined);

  isFlightOnly = computed<boolean>(() => isFlightOnlyTa(this.store.taSubmission()));
  // One-way is stored as start == end, so "Return chosen, second date not yet picked" lives here.
  private flightTripChoice = signal<{taId: string; type: FlightTripType} | undefined>(undefined);
  flightTripType = computed<FlightTripType | null>(() => {
    const ta = this.store.taSubmission();
    const choice = this.flightTripChoice();
    const chosen = choice && choice.taId === ta?.id ? choice.type : null;
    const fromDates = flightTripTypeFromDates(ta?.startDate, ta?.endDate);
    if (fromDates === 'oneWay') {
      return chosen === 'return' ? 'return' : 'oneWay';
    }
    return fromDates ?? chosen;
  });

  constructor(
    private store: TaAppStore,
    private submissionServiceAPI: SubmissionService,
    private adminApi: AdminService,
    private fileServiceAPI: FileService,
    private permissionService: PermissionService,
    private loadingService: LoadingService,
    private router: Router,
    private queue: QueueService,
    private errorMessageService: ErrorMessageService,
  ) {}

  nullLoadServerTaSubmission(id: string) {
    if (!this.store.taSubmission()?.id) {
      this.loadServerTaSubmission(id);
    } else if (this.store.taSubmission()?.id !== id) {
      this.store.resetTaState();
      this.resetSource();
      this.loadServerTaSubmission(id);
    } else {
      //TaSubmission already loaded.
    }
  }

  //Gets new ta from server and adds it to redux
  loadServerTaSubmission(id: string): void {
    // Backstop for callers that fire before the first response lands: nullLoadServerTaSubmission's
    // id check reads store.taSubmission(), so they all see an empty store and all load. Deliberately
    // not cleared by resetSource(), which nullLoadServerTaSubmission calls immediately before this.
    if (this.taLoadInFlight.has(id)) {
      return;
    }
    this.taLoadInFlight.add(id);
    this.taLoadError.set(undefined);
    // this.store.updateLoadingData(true);
    // Not queued: the queue only carries saves. A load must not land behind one - the screens that
    // could trigger it mid-save gate on store.isSavingData() instead.
    const task$ = this.submissionServiceAPI.getFullTASubmissionDetails(id)
      .pipe(finalize(() => this.taLoadInFlight.delete(id)));

    task$.subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.store.updateTaSubmission(response.data);
          this.calculateAllStepsValid();
          this.loadTaDocuments();
        } else {
          // A refused load still returns 200, so this is the only signal a caller waiting on the
          // TA ever gets - without it a view gated on taSubmission() waits forever.
          console.error(response.error);
          this.taLoadError.set({id, message: response.error ?? 'This TA could not be loaded.'});
        }
      },
      error: (error) => {
        console.error('getFullTASubmissionDetails error', error);
        this.taLoadError.set({id, message: 'This TA could not be loaded.'});
        this.store.updateLoadingData(false);
        this.router.navigate(['/']);
      },
      complete: () => {
        console.log('getFullTASubmissionDetails complete');
        this.store.updateLoadingData(false);
      }
    });
  }

  createServerTaSubmission(forMe: boolean, workflowType: TaWorkflowType = TaWorkflowType.Standard): void {
    this.loadingService.showLoading({message: 'Creating Travel Allowance...', spinnerSize: 'md'});
    const task$ = this.submissionServiceAPI.createTaSubmission(forMe, workflowType);

    task$.subscribe({
      next: (response) => {
        this.loadingService.hideLoading();
        if (response.success && response.data?.id) {
          this.store.updateTaSubmission(response.data);
          // A new TA is in no cached list yet, so there is nothing to write through to. Drop the
          // caches it belongs in: history holds every status, and a delegate TA starts InProgress.
          this.store.resetHistoryCache();
          if (!forMe) {
            this.store.resetDelegateCache('active');
          }
          this.calculateAllStepsValid();
          this.router.navigate(['/ta/' + (this.store.taSubmission()?.id)]);
        } else {
          console.error(response.error);
          this.errorMessageService.showTemporaryErrorWithTitle("Create Failed", response.error ?? '', 10000);
        }
      },
      error: (error) => {
        this.loadingService.hideLoading();
        console.error('Creating TA error: ', error);
        this.errorMessageService.showTemporaryError(error, 5000);
      },
      complete: () => {
        // console.log('createTASubmission complete');
      },
    });
  }

  ////////////////////////////////////////////////
  // 'Creating TA' Form Field Functions
  ////////////////////////////////////////////////

  // update fields
  updateTaField(field: string, value: any): void {
    // console.log('updateTAField called', field, value);
    this.store.updateTaSubmissionField(field, value);
  }

  // save fields
  saveTaFieldString(field: string, value: string | null | undefined, localValue: any): void {
    this.updateTaField(field, localValue);
    const isDateField = field === 'startDate' || field === 'endDate';
    let dateSave = 0;
    if (isDateField) {
      // The server resizes (and may re-version) the itinerary, so drop it and refetch the shown or
      // loading one once saved. Nothing shown (e.g. wizard Details step): views load on mount instead.
      // A pending value covers a second date change before the first lands.
      const current = this.store.currentItinerary();
      const shown = current?.submissionId === this.taSubmissionReduxCopy()?.id ? current : undefined;
      this.dateSaveTravellerId = shown?.userTravelId
        ?? this.dateSaveTravellerId
        ?? this.itineraryLoadingInFlight.values().next().value
        ?? (shown ? resolveItineraryTravellerId(this.taSubmissionReduxCopy(), this.store.user()?.id) : undefined);
      dateSave = ++this.dateSaveSeq;
      this.clearItineraryCaches();
      if (this.dateSaveTravellerId) {
        this.store.updateLoadingData(true);
      }
    }
    const submissionId = this.store.taSubmission()!.id!;
    // Refetch on success or failure, only for the latest date save so earlier ones can't fetch pre-resize data.
    const refetchItineraryAfterDateSave = () => {
      if (!isDateField || dateSave !== this.dateSaveSeq) {
        return;
      }
      const travellerId = this.dateSaveTravellerId;
      this.dateSaveTravellerId = undefined;
      if (!travellerId) {
        return;
      }
      const ta = this.taSubmissionReduxCopy();
      if (ta?.id !== submissionId || !ta.startDate || !ta.endDate) {
        this.store.updateLoadingData(false);
        return;
      }
      // Clear again: a fetch started while the save was queued could have cached the pre-resize itinerary.
      this.clearItineraryCaches();
      this.loadTravellerItinerary(travellerId);
    };
    const task$ = this.queue.enqueue(() =>
      this.submissionServiceAPI.updateSubmissionField({
        submissionId: submissionId,
        fieldName: field,
        value: value ?? undefined,
      })
    );

    task$.subscribe({
      next: (response) => {
        // console.log('updateSubmissionField called', response);
        if (response.success && response.data && response.data.details) {
          this.store.updateTaSubmission(response.data.details);
        } else if (!response.success) {
          console.error('updating ta field failed: ' + field, response.error);
          this.errorMessageService.showTemporaryError('Updating TA property failed: ' + field, 5000);
        }
        refetchItineraryAfterDateSave();
      },
      error: (error) => {
        console.error('updateSubmissionField error: ', error);
        this.errorMessageService.showTemporaryError('Error updating TA property: ' + field, 5000);
        refetchItineraryAfterDateSave();
      },
      complete: () => {
        // console.log('updateTASubmission complete');
      }
    });
  }

  setFlightTripChoice(type: FlightTripType): void {
    const taId = this.store.taSubmission()?.id;
    if (taId) {
      this.flightTripChoice.set({taId, type});
    }
  }

  saveTaTravellers(field: string, travellers: Array<TravellerDetails>): void {
    // this.orderTaTravellersPrimary(travellers);
    this.updateTaField(field, travellers);
    const task$ = this.queue.enqueue(() =>
      this.submissionServiceAPI.updateSubmissionField({
        submissionId: this.store.taSubmission()!.id!,
        fieldName: field,
        travellers: travellers,
      })
    );

    task$.subscribe({
      next: (response) => {
        if (response.success && !!response.data) {
          if (!!response.data.details) {
            this.store.updateTaSubmission(response.data.details);
          } else if (!!response.data.objectIds) {
            var trv = travellers.find(t => t.travellerId == null);
            if (!!trv){
              trv.travellerId = response.data.objectIds[0];
              this.updateTaField(field, travellers);
            }
          }
        }
      },
      error: (error) => {
        console.error('updateSubmissionField travellers error: ', error);
      },
      complete: () => {
        // console.log('updateTASubmission complete');
      }
    });
  }

  /**
   * Pre-payment is not offered on day trips. The Travellers step comes before the itinerary, so a
   * flag set there is undone once the itinerary turns out to be a single day.
   * TODO(backend): the server does not enforce this on submit yet.
   */
  clearDayTripPrepayment(): void {
    const ta = this.taSubmissionReduxCopy();
    if (isFlightOnlyTa(ta)) {
      return;
    }
    const itinerary = this.store.currentItinerary();
    if (!ta || ta.status !== 'InProgress' || itinerary?.submissionId !== ta.id || !isDayTripItinerary(itinerary)) {
      return;
    }
    const travellers = ta.travellers ?? [];
    if (!travellers.some(traveller => !!traveller.authorisedAdjustmentDate)) {
      return;
    }
    this.saveTaTravellers('travellers', travellers.map(traveller => ({...traveller, authorisedAdjustmentDate: null})));
  }

  saveTaTraveller(traveller: TravellerDetails): void {
    if (!this.taSubmissionReduxCopy()!.travellers) {
      this.saveTaTravellers('travellers', [traveller]);
      return;
    }

    const index = this.taSubmissionReduxCopy()!.travellers!.findIndex(tt => (!!tt.travellerId && tt.travellerId === traveller.travellerId));
    if (index !== -1) {
      let newTravellers = [...this.taSubmissionReduxCopy()!.travellers!];
      newTravellers[index] = {...traveller};
      this.saveTaTravellers('travellers', newTravellers);
      return;
    }

    let newTravellers = [...this.taSubmissionReduxCopy()!.travellers!.filter(tt => tt.travellerId !== traveller.travellerId), {...traveller}];
    this.saveTaTravellers('travellers', newTravellers);
  }

  loadTaDocuments(): void {
    if (!!this.taSubmissionReduxCopy()?.id) {
      const task$ = this.submissionServiceAPI.getSubmissionFiles(this.taSubmissionReduxCopy()!.id!);
      task$.subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.store.setFileDetails(response.data);
          }
        },
        error: (error) => {
          console.error('loadTaDocuments called ', error);
        }
      })
    } else {
      console.error('No current itinerary loaded.');
    }
  }

  uploadTaDocument(file: File): Observable<number> {
    return new Observable<number>(observer => {
      if (!this.taSubmissionReduxCopy()?.id) {
        observer.error('No submission ID');
        return;
      }
      this.fileServiceAPI.clctaFileSubmissionIdUploadfilePost(this.taSubmissionReduxCopy()!.id!, file, 'events', true).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
            observer.next(progress);
          } else if (event.type === HttpEventType.Response) {
            const response: FileDetailsRestResponse | null = event.body;
            if (response?.success && response.data) {
              this.store.addFileDetails(response.data);
              observer.next(100);
              observer.complete();
            } else {
              console.error('uploadTaDocument errored ', response?.error);
              observer.error(response?.error);
            }
          }
        },
        error: (error) => {
          console.error('uploadTaDocument error ', error);
          observer.error(error);
        },
      });
    });
  }

  downloadTaDocument(fileId: string): void {
    if (!!this.taSubmissionReduxCopy()?.id) {
      const task$ = this.fileServiceAPI.clctaFileDownloadFileIdGet(fileId);
      task$.subscribe({
        next: (response) => {
          console.log(response);
        },
        error: (error) => {
          console.error('removeTaDocument called ', error);
        },
      });
    } else {
      console.error('No current itinerary loaded.');
    }
  }

  removeTaDocument(fileId: string): void {
    if (!!this.taSubmissionReduxCopy()?.id) {
      const task$ = this.fileServiceAPI.clctaFileDeleteFileIdDelete(fileId);

      task$.subscribe({
        next: (response) => {
          this.store.removeFileDetails(fileId);
        },
        error: (error) => {
          console.error('removeTaDocument called ', error);
        },
      });
    } else {
      console.error('No current itinerary loaded.');
    }
  }

  ////////////////////////////////////////////////
  // Itinerary Functions
  ////////////////////////////////////////////////

  loadTravellerItinerary(travellerId: string): void {
    if (!this.taSubmissionReduxCopy()?.startDate || !this.taSubmissionReduxCopy()?.endDate) {
      console.error('No start or end date entered.');
      this.store.updateCurrentItinerary(undefined);
      return;
    }
    const existingItineraryId = [...this.itineraryTravellerMap.entries()]
      .find(([itineraryId, list]) => list.includes(travellerId))?.[0];
    const existingItinerary= !!existingItineraryId ? this.itineraryListMap.get(existingItineraryId) : undefined;
    if (!!existingItinerary) {
      // Deep copy so downstream mutations can never corrupt the cached entry.
      this.store.updateCurrentItinerary(structuredClone(existingItinerary));
    } else if (this.itineraryLoadingInFlight.has(travellerId)) {
      // Duplicate concurrent request — skip; store will update via the in-flight call
      return;
    } else {
      this.itineraryLoadingInFlight.add(travellerId);
      this.store.updateLoadingData(true);
      const generation = this.itineraryCacheGeneration;
      // Caches cleared since this fetch started: its result is stale, and the loading flag now belongs to the refetch.
      const isStale = () => generation !== this.itineraryCacheGeneration;
      // Not queued: reads stay off the save queue. Switching traveller is the only way to reach this
      // while an itinerary save is outstanding, and the traveller tabs are disabled while it is.
      const task$ = this.submissionServiceAPI.getItineraryForUserTravel(travellerId);

      task$.subscribe({
        next: (response: UserTravelItineraryRestResponse) => {
          if (isStale()) {
            return;
          }
          if (response.success && !!response.data && !!response.data.id) {
            this.setItineraryListMap(response.data.id, response.data);
            this.updateTravellerMap(response.data);
            this.store.updateCurrentItinerary(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Failed to get itinerary for user' + response.error, 5000);
            console.error('Failed to get itinerary for user', response.error);
          }
        },
        error: (error) => {
          console.error('getItineraryForUser error: ', error);
          if (isStale()) {
            return;
          }
          this.store.updateLoadingData(false);
          this.itineraryLoadingInFlight.delete(travellerId);
        },
        complete: () => {
          if (isStale()) {
            return;
          }
          this.store.updateLoadingData(false);
          this.itineraryLoadingInFlight.delete(travellerId);
        }
      });
    }
  }

  addCurrentItineraryItem(itineraryItem?: ItineraryItemDetails): void {
    const task$ = this.queue.enqueue(() =>
      this.submissionServiceAPI.updateItineraryItem(itineraryItem)
    );

    task$.subscribe({
      next: (response) => {
        if (response && response.success && !!response.data) {
          if (response.data.itineraryResult && response.data.itineraryResult.id) {
            this.updateTravellerMap(response.data.itineraryResult);
            this.store.updateCurrentItinerary(response.data.itineraryResult);
          } else if (response.data.itemResult) {
            this.store.addCurrentItineraryItem(response.data.itemResult);
          } else {
            this.errorMessageService.showTemporaryError('Server error in adding itinerary item, incomplete response data: ' + response.error, 5000);
            console.error('Server error in adding itinerary item, incomplete response data: ', response.data, response.error);
          }
          if (!!this.store.currentItinerary()?.id) {
            this.setItineraryListMap(this.store.currentItinerary()!.id!, this.store.currentItinerary()!);
          }
        } else {
          console.error('Server error on adding itinerary item: ', response?.error);
        }
      },
      error: (error) => {
        console.error('updateTASubmission error: ', error);
      }
    });
  }

  updateTravellerMap(newItinerary: UserTravelItinerary): void {
    const itineraryId = newItinerary.id;
    const travellerId = newItinerary.userTravelId!;

    if (!!itineraryId && !!travellerId) {
      // Remove the traveller from any other arrays
      for (const [id, arr] of this.itineraryTravellerMap.entries()) {
        if (id !== itineraryId) {
          this.itineraryTravellerMap.set(id, arr.filter(tid => tid !== travellerId));
        }
      }
      // Add the traveller to the target itinerary
      const currentList = this.itineraryTravellerMap.get(itineraryId) || [];
      if (!currentList.includes(travellerId)) {
        currentList.push(travellerId);
        this.itineraryTravellerMap.set(itineraryId, currentList);
      }
    }
  }

  // itemType === 'mealClaims' keeps the optimistic mealClaims and takes only the recalculated
  // allowance amounts off the response - see TaAppStore.updateCurrentItineraryItemValues. That
  // shortcut only holds while the response is for the itinerary we already have; see below.
  saveCurrentItineraryItem(itineraryItem: ItineraryItemDetails, itemType?: string): void {
    // Optimistic write before enqueuing, as saveTaFieldString does
    if (!!itineraryItem.id) {
      this.store.updateCurrentItineraryItem(itineraryItem);
    }

    const task$ = this.queue.enqueue(() =>
      this.submissionServiceAPI.updateItineraryItem(itineraryItem)
    );

    task$.subscribe({
      next: (response) => {
        if (response.success && !!response.data) {
          if (response.data.itineraryResult) {
            const itineraryResult = response.data.itineraryResult;
            this.updateTravellerMap(itineraryResult);
            // itineraryResult comes back for two different reasons: meal rules touched other rows,
            // or the server forked the itinerary (copy-on-write for a non-primary traveller, or a
            // new version when amending after submission). A fork carries a new itinerary id and
            // new item ids, so there is nothing to merge onto by id and no open control to protect
            // from flicker - the whole itinerary legitimately changed, so adopt it wholesale.
            const sameItinerary = !!itineraryResult.id && itineraryResult.id === this.store.currentItinerary()?.id;
            if (itemType === 'mealClaims' && sameItinerary) {
              this.store.updateCurrentItineraryItemValues(itineraryResult.items ?? []);
            } else {
              this.store.updateCurrentItinerary(itineraryResult);
            }
          } else if (response.data.itemResult) {
            if (itemType === 'mealClaims') {
              this.store.updateCurrentItineraryItemValues([response.data.itemResult]);
            } else {
              this.store.updateCurrentItineraryItem(response.data.itemResult);
            }
          } else {
            this.errorMessageService.showTemporaryError('Server error in saving itinerary item, incomplete response data: ' + response.error, 5000);
            console.error('Server error in saving itinerary item, incomplete response data: ', response.data, response.error);
          }

          if (!!this.store.currentItinerary()?.id) {
            this.setItineraryListMap(this.store.currentItinerary()!.id!, this.store.currentItinerary()!);
          }
        } else {
          // The optimistic write above is already on screen, so a silent failure would leave the
          // user looking at a value the server never took.
          this.errorMessageService.showTemporaryError('Saving itinerary item failed: ' + response.error, 5000);
          console.error('Server error on saving itinerary item: ', response.error);
        }
      },
      error: (error) => {
        this.errorMessageService.showTemporaryError('Error saving itinerary item.', 5000);
        console.error('updateTASubmission error: ', error);
      }
    });
  }

  saveCurrentItinerary(itinerary: UserTravelItinerary): void {
    const task$ = this.queue.enqueue(() =>
      this.submissionServiceAPI.updateItinerary(itinerary)
    );

    task$.subscribe({
      next: (response) => {
        if (response.success && !!response.data) {
          if (response.data.itineraryResult) {
            this.updateTravellerMap(response.data.itineraryResult);
            this.store.updateCurrentItinerary(response.data.itineraryResult);
          } else if (response.data.itemResult) {
            console.error('Server error in saving itinerary, wrong response data: ', response.data, response.error);
          } else {
            this.errorMessageService.showTemporaryError('Server error in saving itinerary, incomplete response data: ' + response.error, 5000);
            console.error('Server error in saving itinerary, incomplete response data: ', response.data, response.error);
          }

          if (!!this.store.currentItinerary()?.id) {
            this.setItineraryListMap(this.store.currentItinerary()!.id!, this.store.currentItinerary()!);
          }
        } else {
          console.error('Server error on saving itinerary: ', response.error);
        }
      },
      error: (error) => {
        console.error('updateTASubmission error: ', error);
      }
    });
  }

  removeCurrentItineraryItem(itineraryItem: ItineraryItemDetails): void {
    if (this.store.currentItinerary()?.id && itineraryItem.id) {
      const task$ = this.queue.enqueue(() =>
        this.submissionServiceAPI.deleteItineraryItem(itineraryItem)
      );

      task$.subscribe({
        next: (response) => {
          if (response.success && !!response.data) {
            if (response.data.itineraryResult) {
              this.updateTravellerMap(response.data.itineraryResult);
              this.store.updateCurrentItinerary(response.data.itineraryResult);
            } else {
              this.store.removeCurrentItineraryItem(itineraryItem.id ?? '');
            }
            if (!!this.store.currentItinerary()?.id) {
              this.setItineraryListMap(this.store.currentItinerary()!.id!, this.store.currentItinerary()!);
            }
          } else {
            this.errorMessageService.showTemporaryError('Server error on removing itinerary item: ' + response.error, 5000);
            console.error('Server error on removing itinerary item: ', response.error);
          }
        },
        error: (error) => {
          console.error('removeCurrentItineraryItem called', error);
        }
      });
    }
  }

  setManualMode(itineraryId: string, isManualMode: boolean): void {
    this.submissionServiceAPI.setItineraryManualMode(itineraryId, isManualMode).subscribe({
      next: (response) => {
        if (response.success && !!response.data) {
          this.store.updateCurrentItinerary({...this.store.currentItinerary(), isManual: isManualMode});
          if (!!this.store.currentItinerary()?.id) {
            this.setItineraryListMap(this.store.currentItinerary()!.id!, this.store.currentItinerary()!);
          }
        } else {
          console.error('Error setting manual mode: ', response.error);
        }
      },
      error: (error) => {
        console.error('setManualMode called', error);
      }
    });
  }

  updateItineraryNotes(notes: string): void {
    const current = this.store.currentItinerary();
    if (!current?.id) return;
    this.store.updateCurrentItinerary({ ...current, notes });
    this.saveCurrentItinerary({ ...current, notes });
  }

  setItineraryListMap(id: string, itinerary: UserTravelItinerary): void {
    let linkedTravellers = itinerary.linkedUserTravellers ?? []
    let primaryTravelId = this.taSubmissionReduxCopy()?.primaryTraveller?.travellerId;
    if (!!primaryTravelId && linkedTravellers.includes(primaryTravelId)) {
      this.primaryLinkedUserTravellers.set(itinerary.linkedUserTravellers ?? [])
    } else if (linkedTravellers.length === 1) {
      this.primaryLinkedUserTravellers.set(this.primaryLinkedUserTravellers().filter(value => value !== linkedTravellers.at(0)));
    }
    // Deep copy so cached entries are isolated from live store/display objects.
    this.itineraryListMap.set(id, structuredClone(itinerary));
  }

  getAllItineraries() {
    const task$ = this.submissionServiceAPI.getAllTravellerItineraries(this.taSubmissionReduxCopy()!.id!);

    task$.subscribe({
      next: (response) => {
        if (response.success && !!response.data) {
          this.allItineraries.set(response.data);
        } else {
          console.error('Error getting full itinerary list for acquittal approval: ', response.error);
        }
      },
      error: (error) => {
        console.error('removeCurrentItineraryItem called', error);
      }
    });
  }

  ////////////////////////////////////////////////
  // Validation Functions
  // calculateAllStepsValid: Calls validation for every step
  // calculateStepCodeValid: Calls validation function for a given step
  // calculateCurrentStepValid: Calls validation for current step
  // calculateStepValid: Helper function used by above
  // calculateStepEnabled: Checks which steps are available (for the timeline)
  // isTravellersValid: Checks if travellers step has valid data
  // isDetailsValid: Checks if details step has valid data
  // isItineraryValid: Checks if itinerary step has valid data
  // isDocumentationValid: Checks if correct documents are uploaded
  // isReviewValid: Checks entire ta/ any extra business rules
  ////////////////////////////////////////////////
  calculateAllStepsValid(): void {
    this.store.createTaSteps().forEach((step) => {
      this.calculateStepValid(step);
    });
    this.calculateStepEnabled();
  }

  calculateStepCodeValid(stepType: CreateTaStepType): void {
    let step = this.store.createTaSteps().find(t => t.code === stepType);
    if (!step || !this.store.taSubmission()) {
      return;
    }
    this.calculateStepValid(step);
    this.calculateStepEnabled();
  }

  calculateCurrentStepValid(): void {
    let step = this.store.currentCreateTaStep();
    if (!this.store.taSubmission() || !this.store.currentCreateTaStep) {
      return;
    }
    this.calculateStepValid(step);
    this.calculateStepEnabled();
  }

  calculateStepValid(step: CreateTaStep): void {
    let response = {isValid: false};
    switch(step.code) {
      case CreateTaStepType.TRAVELLERS:
        response = this.isTravellersValid();
        break;
      case CreateTaStepType.DETAILS:
        response = this.isDetailsValid()
        break;
      case CreateTaStepType.ITINERARY:
        response = this.isItineraryValid()
        break;
      case CreateTaStepType.DOCUMENTATION:
        response = this.isDocumentationValid()
        break;
      case CreateTaStepType.REVIEW:
        response = this.isReviewValid()
        break;
    }
    step.valid = response.isValid;
  }

  calculateStepEnabled(): void {
    let createTaSteps = this.store.createTaSteps();
    createTaSteps.forEach((step, index) => {
      if (step.code === this.store.currentCreateTaStep().code) {
        step.enabled = true;
      }
      else if (step.valid) {
        //Step is clickable if it is currently valid
        step.enabled = true;
      } else {
        //Step is enabled if everything before it is valid
        step.enabled = true;
        for (let ii = index; ii--; ii >= 0) {
          if (!createTaSteps[ii].valid) {
            step.enabled = false;
          }
        }
      }
      if (step.code === CreateTaStepType.REVIEW) {
        step.enabled = true;
      }
    });
  }

  isTravellersValid(): ValidatorResponse {
    let validatorResponse: ValidatorResponse = {
      isValid: true,
      message: '',
    };
    let taSubmission = this.store.getTaSubmission();
    if (!taSubmission.travellers || taSubmission.travellers.length === 0) {
      validatorResponse.isValid = false;
      validatorResponse.message = 'Not enough travellers.';
    }
    return validatorResponse;
  }

  isDetailsValid(): ValidatorResponse {
    let validatorResponse: ValidatorResponse = {
      isValid: true,
      message: '',
    }
    let taSubmission = this.store.getTaSubmission();
    if (isFlightOnlyTa(taSubmission)) {
      return this.isFlightOnlyDetailsValid(taSubmission);
    }
    if (!taSubmission.projectId) {
      validatorResponse.isValid = false;
      validatorResponse.message = 'Project is required.';
    }

    // Keyed off the primary traveller's groups, as the details step and the server's submit check are.
    const travellerGroups = taSubmission.primaryTraveller?.details?.permissionSummary?.groups ?? [];
    if (travellerGroups.some(group => group.code === 'ceo' || group.code === 'generalManagers')) {
      if (!taSubmission.primaryApprover) {
        validatorResponse.isValid = false;
        validatorResponse.message = 'Approver is required.';
      }
    } else {
      if (!taSubmission.secondaryApprover) {
        validatorResponse.isValid = false;
        validatorResponse.message = 'Approver is required.';
      }
    }
    if (!taSubmission.purpose) {
      validatorResponse.isValid = false;
      validatorResponse.message = 'Purpose is required.';
    }
    if (!taSubmission.purposeNotes) {
      validatorResponse.isValid = false;
      validatorResponse.message = 'Purpose notes are required.';
    }
    if (!taSubmission.startDate || !taSubmission.endDate || Date.parse(taSubmission.endDate) < Date.parse(taSubmission.startDate) ) {
      validatorResponse.isValid = false;
      validatorResponse.message = 'End date must be after start date.';
    }
    return validatorResponse;
  }

  // Server sets the line manager as primary approver; secondaryApprover is never used.
  private isFlightOnlyDetailsValid(taSubmission: FullTaDetails): ValidatorResponse {
    const invalid = (message: string): ValidatorResponse => ({isValid: false, message});
    if (!taSubmission.projectId) {
      return invalid('Project is required.');
    }
    if (!taSubmission.purpose) {
      return invalid('Purpose is required.');
    }
    if (!taSubmission.purposeNotes) {
      return invalid('Purpose notes are required.');
    }
    if (!taSubmission.startDate || !taSubmission.endDate || Date.parse(taSubmission.endDate) < Date.parse(taSubmission.startDate)) {
      return invalid('End date must be after start date.');
    }
    if (this.flightTripType() === 'return' && flightTripTypeFromDates(taSubmission.startDate, taSubmission.endDate) !== 'return') {
      return invalid('Select the second flight date.');
    }
    if (!taSubmission.primaryApprover) {
      const travellerGroups = taSubmission.primaryTraveller?.details?.permissionSummary?.groups ?? [];
      return invalid(travellerGroups.some(group => group.code === 'ceo' || group.code === 'generalManagers')
        ? 'A general manager approver must be selected.'
        : 'Approver is required.');
    }
    return {isValid: true, message: ''};
  }

  isItineraryValid(): ValidatorResponse {
    let validatorResponse: ValidatorResponse = {
      isValid: true,
      message: '',
    }

    // let itinerary: UserTravelItinerary | undefined = this.store.currentItinerary();
    //
    // if (!itinerary) {
    //   validatorResponse.isValid = false;
    //   validatorResponse.message = 'Itinerary not loaded yet.';
    //   return validatorResponse;
    // }
    // if (itinerary.items?.length === 0) {
    //   validatorResponse.isValid = false;
    //   validatorResponse.message = 'No items in itinerary.';
    //   return validatorResponse;
    // }
    // for (let itineraryItem of itinerary.items!) {
    //   let item = itineraryItem as  ItineraryItemDetails;
    //   if (!item.startLocationNameId) {
    //     validatorResponse.isValid = false;
    //     validatorResponse.message = 'Start location is required.';}
    //   if (!item.endLocationNameId) {
    //     validatorResponse.isValid = false;
    //     validatorResponse.message = 'End location is required.';}
    //   if (!item.travelMode) {
    //     validatorResponse.isValid = false;
    //     validatorResponse.message = 'Travel mode is required.';
    //     return validatorResponse;
    //   } else if (!item.accommodationType) {
    //     validatorResponse.isValid = false;
    //     validatorResponse.message = 'Accommodation Type is required.';
    //     return validatorResponse;
    //   }
    // }

    return validatorResponse;
  }

  isDocumentationValid(): ValidatorResponse {
    let validatorResponse: ValidatorResponse = {
      isValid: true,
      message: '',
    };

    return validatorResponse;
  }

  isReviewValid(): ValidatorResponse {
    let validatorResponse: ValidatorResponse = {
      isValid: true,
      message: '',
    };
    this.store.createTaSteps().forEach(step => {
      if (step.code !== CreateTaStepType.REVIEW && !step.valid) {
        validatorResponse = {
          isValid: false,
          message: 'Step ' + step.title + ' is invalid',
        }
      }
    });
    return validatorResponse;
  }

  ////////////////////////////////////////////////
  // Manage TA Functions
  // approveTa: Controls approving the newly created TA submission
  ////////////////////////////////////////////////
  approveTa(action: 'approve' | 'withhold' | 'reject', notes: string, taSubmission?: FullTaDetails | BasicTaDetails | undefined): Observable<any> {
    if (action === 'approve') {
      return this.submissionServiceAPI.approveSubmission({
        submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
        notes: notes
      }).pipe(tap({
        next: (response) => {
          if (response && response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Server error on approving TA: ' + response.error, 5000);
            console.error('Server error on approving TA: ', response);
          }
        },
        error: (error) => {
          console.error('updateTASubmission error: ', error);
        }
      }));
    } else if (action === 'withhold') {
      return this.submissionServiceAPI.withholdSubmission({
        submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
        notes: notes,
        withholdUserTravelIds: [],
      }).pipe(tap({
        next: (response) => {
          if (response && response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Server error on withholding TA: ' + response.error, 5000);
            console.error('Server error on withholding TA: ', response);
          }
        },
        error: (error) => {
          console.error('updateTASubmission error: ', error);
        }
      }));
    } else if (action === 'reject') {
      return this.submissionServiceAPI.rejectSubmission({
        submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
        notes: notes
      }).pipe(tap({
        next: (response) => {
          if (response && response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Server error on rejecting TA: ' + response.error, 5000);
            console.error('Server error on rejecting TA: ', response);
          }
        },
        error: (error) => {
          console.error('updateTASubmission error: ', error);
        }
      }));
    }
    return of(null);
  }

  processTaPrepayment(taSubmission?: FullTaDetails | BasicTaDetails): Observable<any> {
    const submissionId = taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!;
    return this.adminApi.actionPrepayment({
      submissionId,
    }).pipe(tap({
      next: (response) => {
        if (response && response.data) {
          if (response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          }
        } else {
          console.error('Server error on processing prepayment: ', response);
        }
      },
      error: (error) => {
        console.error('processTaPrepayment error: ', error);
      }
    }));
  }

  /**
   * Replaces one or more approvers on a single TA. Finance/admin only - the server enforces the
   * CanOverrideApprovers policy and rejects overrides it cannot apply, so callers should only act
   * on a successful response.
   */
  overrideApprovers(details: SubmissionApproversDetails): Observable<SubmissionApproversDetailsRestResponse> {
    return this.adminApi.overrideApprovers(details).pipe(tap({
      next: (response) => {
        if (!response?.success) {
          this.errorMessageService.showTemporaryErrorWithTitle('Update Failed', response?.error ?? '', 10000);
          console.error('Server error on overriding approvers: ', response);
        }
      },
      error: (error) => {
        this.errorMessageService.showTemporaryErrorWithTitle('Update Failed', 'Server error updating approvers.', 10000);
        console.error('overrideApprovers error: ', error);
      }
    }));
  }

  acquitTa(userTravelId: string, notes: string): void {
    // Not queued: acquitting locks in the claimed values, so it must not run behind a save. The
    // Acquit button is disabled while store.isSavingData() is true, so there is nothing to overtake.
    let task$ = this.submissionServiceAPI.acquitUserTravel({
      submissionId: this.taSubmissionReduxCopy()!.id!,
      userTravelId: userTravelId,
      notes: notes,
    });
    task$.subscribe({
      next: (response) => {
        if (response && response.data) {
          if (response.data.taDetails) {
            this.store.updateTaSubmissionPartial(response.data.taDetails);
          }
          if (response.data.userTravel) {
            this.saveTaTraveller(response.data.userTravel);
          }
        } else {
          console.error('Server error on acquitting travel: ', response);
          this.errorMessageService.showTemporaryError('Server error on acquitting: ' + response.error, 5000);
        }
      },
      error: (error) => {
        console.error('Error acquitting travel: ', error);
        this.errorMessageService.showTemporaryError('Server error on acquitting: ' + (error?.message ?? ''), 5000);
      }
    });
  }

  approveTaAcquittal(action: 'approve' | 'withhold', notes: string, taSubmission?: FullTaDetails | BasicTaDetails | undefined): Observable<any> {
    if (action === 'approve') {
      return this.submissionServiceAPI.approveAcquittedSubmission({
        submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
        notes: notes,
      }).pipe(tap({
        next: (response) => {
          if (response && response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Server error on approving acquittal TA: ' + response.error, 5000);
            console.error('Server error on approving acquittal TA: ', response);
          }
        },
        error: (error) => {
          console.error('updateTASubmission error: ', error);
        }
      }));
    } else if (action === 'withhold') {
      return this.submissionServiceAPI.withholdAcquittedSubmission({
        submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
        notes: notes,
      }).pipe(tap({
        next: (response) => {
          if (response && response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Server error on withholding acquittal TA: ' + response.error, 5000);
            console.error('Server error on withholding acquittal TA: ', response);
          }
        },
        error: (error) => {
          console.error('updateTASubmission error: ', error);
        }
      }));
    }
    return of(null);
  }

  // PendingCancelApproval -> CancelApproved once every assigned cancellation approver has actioned.
  // Approve-only by design: a cancellation cannot be withheld or rejected.
  //
  // The error branch toasts because this endpoint rejects with a 400 (ApproveCancellationSubmission
  // throws BadHttpRequestException for an already-cancelled TA), which ErrorInterceptor does not
  // report - without this the acknowledge button fails silently.
  approveTaCancellation(notes: string, taSubmission?: FullTaDetails | BasicTaDetails | undefined): Observable<any> {
    return this.submissionServiceAPI.approveCancelSubmission({
      submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
      notes: notes,
    }).pipe(tap({
      next: (response) => {
        if (response && response.data) {
          this.store.updateTaSubmissionPartial(response.data);
        } else {
          this.errorMessageService.showTemporaryErrorWithTitle('Acknowledgement Failed',
            response?.error ?? 'The cancellation could not be acknowledged.', 10000);
          console.error('Server error on approving TA cancellation: ', response);
        }
      },
      error: (error) => {
        // The server's own message, as ErrorInterceptor reads it - a rejected acknowledgement says
        // why ("already been cancelled"), which is more use than a generic failure.
        this.errorMessageService.showTemporaryErrorWithTitle('Acknowledgement Failed',
          error?.error?.error || 'The cancellation could not be acknowledged.', 10000);
        console.error('approveTaCancellation error: ', error);
      }
    }));
  }

  // PendingCancelApproval | CancelApproved -> TravelApproved | PendingAcquittal. Finance/admin
  // undoes a cancellation raised in error: the server drops the cancellation and overpayment
  // approvals, restores the value an approved cancellation had zeroed, and recomputes the status
  // from the travel dates. A reason is mandatory - the endpoint 400s without one.
  //
  // Toasts on error for the same reason approveTaCancellation does: the endpoint rejects a status
  // race with a 400, which ErrorInterceptor does not report, so the button would fail silently.
  revertTaCancellation(notes: string, taSubmission?: FullTaDetails | BasicTaDetails | undefined): Observable<any> {
    return this.adminApi.revertPrepayCancel({
      submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
      notes: notes,
    }).pipe(tap({
      next: (response) => {
        if (response && response.data) {
          this.store.updateTaSubmissionPartial(response.data);
        } else {
          this.errorMessageService.showTemporaryErrorWithTitle('Reversal Failed',
            response?.error ?? 'The cancellation could not be reversed.', 10000);
          console.error('Server error on reverting TA cancellation: ', response);
        }
      },
      error: (error) => {
        // The server's own message says why a reversal was refused ("its status changed from ...").
        this.errorMessageService.showTemporaryErrorWithTitle('Reversal Failed',
          error?.error?.error || 'The cancellation could not be reversed.', 10000);
        console.error('revertTaCancellation error: ', error);
      }
    }));
  }

  // Finance/travel officer cancelling a prepayment request (CLC-191). A reason is mandatory, and the
  // server 400s on a status or not-prepaid refusal, which ErrorInterceptor does not report.
  cancelTaPrepayment(notes: string, taSubmission?: FullTaDetails | BasicTaDetails | undefined): Observable<any> {
    return this.adminApi.cancelPrepayment({
      submissionId: taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!,
      notes: notes,
    }).pipe(tap({
      next: (response) => {
        if (response && response.data) {
          this.store.updateTaSubmissionPartial(response.data);
        } else {
          this.errorMessageService.showTemporaryErrorWithTitle('Prepayment Cancellation Failed',
            response?.error ?? 'The prepayment could not be cancelled.', 10000);
          console.error('Server error on cancelling TA prepayment: ', response);
        }
      },
      error: (error) => {
        this.errorMessageService.showTemporaryErrorWithTitle('Prepayment Cancellation Failed',
          error?.error?.error || 'The prepayment could not be cancelled.', 10000);
        console.error('cancelTaPrepayment error: ', error);
      }
    }));
  }

  cancelSubmission(userTravelId: string, notes: string): Observable<any> {
    return this.submissionServiceAPI.cancelTASubmission({
      submissionId: this.taSubmissionReduxCopy()!.id!,
      userTravelId: userTravelId,
      notes: notes,
    }).pipe(tap({
      next: (response) => {
        if (response && response.data) {
          this.store.updateTaSubmissionPartial(response.data);
        } else {
          this.errorMessageService.showTemporaryError('Server error on cancelling TA: ' + response.error, 5000);
          console.error('Server error on cancelling TA: ', response);
        }
      },
      error: (error) => {
        console.error('cancelTaSubmission error: ', error);
      }
    }));
  }

  deleteInProgressSubmission(): Observable<any> {
    return this.submissionServiceAPI.deleteInProgressTASubmission({
      submissionId: this.taSubmissionReduxCopy()!.id!,
    }).pipe(tap({
      next: (response) => {
        if (response && response.data) {
          this.store.updateTaSubmissionPartial(response.data);
        } else {
          this.errorMessageService.showTemporaryError('Server error on deleting TA: ' + response.error, 5000);
          console.error('Server error on deleting TA: ', response);
        }
      },
      error: (error) => {
        console.error('deleteInProgressSubmission error: ', error);
      }
    }));
  }

  processTaOverpaymentDeduction(taSubmission?: FullTaDetails | BasicTaDetails): Observable<any> {
    const submissionId = taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!;
    return this.adminApi.actionDeduction({
      submissionId,
    }).pipe(tap({
      next: (response) => {
        if (response && response.data) {
          if (response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          }
        } else {
          console.error('Server error on processing overpayment deduction: ', response);
        }
      },
      error: (error) => {
        console.error('processTaOverpaymentDeduction error: ', error);
      }
    }));
  }

  /**
   * Batch finance actions. The server skips ids it cannot action (already processed, no longer
   * eligible, a lost race) rather than failing the call, so a partial result is reported here as a
   * warning; the caller only has to reload its list.
   */
  finaliseSubmissionsBatch(taSubmissions: BasicTaDetails[]): Observable<SubmissionBatchResultRestResponse> {
    const submissionIdList = this.submissionIds(taSubmissions);
    return this.adminApi.finaliseSubmissionBatch({submissionIdList}).pipe(tap({
      next: response => this.reportBatchOutcome(response, submissionIdList.length, 'finalising', 'finalised'),
      error: error => {
        console.error('finaliseSubmissionsBatch error: ', error);
      },
    }));
  }

  processTaPrepaymentsBatch(taSubmissions: BasicTaDetails[]): Observable<SubmissionBatchResultRestResponse> {
    const submissionIdList = this.submissionIds(taSubmissions);
    return this.adminApi.actionPrepaymentBatch({submissionIdList}).pipe(tap({
      next: response => this.reportBatchOutcome(response, submissionIdList.length, 'processing prepayments', 'marked as prepayment processed'),
      error: error => {
        console.error('processTaPrepaymentsBatch error: ', error);
      },
    }));
  }

  // One compliance dashboard list tab (first 50 rows), plus the counts for every tab.
  loadComplianceDashboardTab(tab: ComplianceDashboardTab): Observable<ComplianceDashboardTabResultRestResponse> {
    return this.adminApi.getSubmissionsForComplianceDashboard(tab).pipe(tap({
      next: response => {
        if (!response?.success) {
          this.errorMessageService.showTemporaryError('Server error loading compliance dashboard: ' + response?.error, 5000);
        }
      },
      error: error => {
        console.error('loadComplianceDashboardTab error: ', error);
      },
    }));
  }

  // Records the $45 day trip payment as processed. The server also finalises the TA when every
  // traveller is on a single day trip; otherwise Finance still finalises it.
  processDayTripPayment(taSubmission?: FullTaDetails | BasicTaDetails, notes?: string): Observable<FullTaDetailsRestResponse> {
    const submissionId = taSubmission?.id ?? this.taSubmissionReduxCopy()!.id!;
    return this.adminApi.actionDayTripPayment({submissionId, notes}).pipe(tap({
      next: (response) => {
        if (response?.data) {
          this.store.updateTaSubmissionPartial(response.data);
        } else {
          this.errorMessageService.showTemporaryErrorWithTitle('Processing Failed', response?.error ?? 'The day trip payment could not be processed.', 10000);
          console.error('Server error on processing day trip payment: ', response);
        }
      },
      error: (error) => {
        // ErrorInterceptor already reports the 500 the store raises for a user it rejects.
        console.error('processDayTripPayment error: ', error);
      }
    }));
  }

  // TODO(backend): placeholder until the day trip batch endpoint exists - does nothing. Wire like
  // processTaPrepaymentsBatch: adminApi.actionDayTripPaymentBatch({submissionIdList}) + reportBatchOutcome
  // ('processing day trip payments', 'marked as day trip payment processed').
  processDayTripPaymentsBatch(_taSubmissions: BasicTaDetails[]): Observable<SubmissionBatchResultRestResponse> {
    return EMPTY;
  }

  private submissionIds(taSubmissions: BasicTaDetails[]): string[] {
    return taSubmissions.map(taSubmission => taSubmission.id).filter((id): id is string => !!id);
  }

  private reportBatchOutcome(response: SubmissionBatchResultRestResponse, requested: number, verbing: string, pastTense: string): void {
    if (!response?.data) {
      this.errorMessageService.showTemporaryError('Server error on ' + verbing + ': ' + response?.error, 5000);
      console.error('Server error on ' + verbing + ': ', response);
      return;
    }
    const updated = response.data.updatedCount ?? 0;
    if (updated < requested) {
      this.errorMessageService.showWarningMessage(
        updated + ' of ' + requested + ' selected TAs were ' + pastTense + '. The rest were no longer eligible and were skipped.',
        'Some TAs Skipped', 8000);
    }
  }

  finaliseSubmission(action: 'finalise' | 'withhold', notes: string, taSubmission?: FullTaDetails | BasicTaDetails | undefined): Observable<any> {
    if (!taSubmission?.id) {
      return of(null);
    }
    if (action === 'finalise') {
      return this.adminApi.finaliseSubmission({
        submissionId: taSubmission.id!,
      }).pipe(tap({
        next: (response) => {
          if (response && response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Server error on finalising TA: ' + response.error, 5000);
            console.error('Server error on finalising TA: ', response);
          }
        },
        error: (error) => {
          console.error('finaliseSubmission error: ', error);
        }
      }));
    } else if (action === 'withhold') {
      return this.adminApi.resetFinalise({
        submissionId: taSubmission.id!,
        notes: notes,
      }).pipe(tap({
        next: (response) => {
          if (response && response.data) {
            this.store.updateTaSubmissionPartial(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Server error on resetting finalisation TA: ' + response.error, 5000);
            console.error('Server error on resetting finalisation TA: ', response);
          }
        },
        error: (error) => {
          console.error('resetFinalise error: ', error);
        }
      }));
    }
    return of(null);
  }

  submitTAForApproval(submissionId: string | undefined, notes: string) {
    const key = submissionId ?? '';
    if (this.submitInFlight.has(key)) {
      // Duplicate concurrent submit — skip; the in-flight call's subscriber handles the result
      return EMPTY;
    }
    // Not queued: submitting must not land behind a save, so the review page's Submit button is
    // disabled while store.isSavingData() is true. The guard is set inside defer because what we
    // return is cold - setting it at call time would block submit for good if a caller never
    // subscribed.
    return defer(() => {
      this.submitInFlight.add(key);
      return this.submissionServiceAPI.submitTAForApproval({submissionId, notes});
    }).pipe(tap({
      // Merged here, not by the caller: clc-review-ta-page resets the TA state and navigates away, so
      // the cached lists kept showing In Progress. tap runs before the caller's handler, so the store
      // is already updated by the time the page acts on the response.
      next: response => {
        this.submitInFlight.delete(key);
        if (response?.success && response.data) {
          this.store.updateTaSubmissionPartial(response.data);
        }
      },
      error: () => this.submitInFlight.delete(key),
    }));
  }

  loadSubmissionAuditLog(submissionId: string) {
    return this.submissionServiceAPI.getSubmissionAuditLog(submissionId);
  }

  ////////////////////////////////////////////////
  // TA Value Functions
  ////////////////////////////////////////////////
  updateTaEstimateValue(taSubmissionId: string): void {
    // Flight-only TAs have no allowance to estimate.
    if (this.isFlightOnly()) {
      return;
    }
    const task$ = this.queue.enqueue(() =>
      this.submissionServiceAPI.getSubmissionEstimate(taSubmissionId)
    );

    task$.subscribe({
      next: response => {
        if (response.success && response.data) {
          this.store.updateTaValue(response.data);
        }
      },
      error: error => {
        console.error(error);
      },
      complete: () => {}
    });
  }

  // isPrePaid is server-derived from the travellers, so an edit can change it. Queued like the
  // estimate so it reads after the edit's saves; patches only the prepaid fields (a full merge
  // would blank the store's approvals).
  updateTaPrepaidStatus(taSubmissionId: string): void {
    const task$ = this.queue.enqueue(() =>
      this.submissionServiceAPI.getBasicTASubmissionDetails(taSubmissionId)
    );

    task$.subscribe({
      next: response => {
        // Dropped if the user has moved to another TA.
        if (response.success && response.data
          && response.data.id?.toLowerCase() === this.store.taSubmission()?.id?.toLowerCase()) {
          const {isPrePaid, prePaymentProcessed, prePaidValue} = response.data;
          this.store.updateTaSubmissionPartial({isPrePaid, prePaymentProcessed, prePaidValue});
        }
      },
      error: error => {
        console.error(error);
      },
    });
  }

  ////////////////////////////////////////////////
  // Util Functions
  // logStore: Logs useful store values
  // resetSource: Reset store to default values
  ////////////////////////////////////////////////
  logStore(): void {
    console.log('logStore.1', this.itineraryTravellerMap);
    console.log('logStore.2', this.itineraryListMap);
    console.log('logStore.3', this.taSubmissionReduxCopy());
    console.log('logStore.4', this.store.currentItinerary());
  }

  resetSource(): void {
    this.flightTripChoice.set(undefined);
    this.itineraryListMap.clear();
    this.itineraryTravellerMap.clear();
    this.itineraryLoadingInFlight.clear();
  }

  clearItineraryCaches(): void {
    this.itineraryCacheGeneration++;
    this.itineraryListMap.clear();
    this.itineraryTravellerMap.clear();
    this.itineraryLoadingInFlight.clear();
    this.store.updateCurrentItinerary(undefined);
  }

  findLocation(locationId: string, nameId: string): LocationOption | undefined {
    let location: LocationOption | undefined;
    if (!!nameId && !!locationId) {
      location = this.store.availableLocations().find(location => (location.nameId === nameId && location.locationId === locationId));
    }
    if (!location && !!locationId) {
      location = this.store.availableLocations().find(location => location.locationId === locationId);
    }
    return location;
  }
}
